export type HighlightStyle = {
  containerHover: string;
  containerSelected: string;
  labelHover: string;
  labelSelected: string;
};

type AccentColor =
  | "orange"
  | "violet"
  | "magenta"
  | "blue"
  | "gray"
  | "yellow"
  | "green"
  | "cyan"
  | "red";

/**
 * 하이라이트 키 → accent 색상 매핑
 * - DP에서는 현재 element.category를 하이라이트 키로 사용
 * - 새 하이라이트 타입 추가 시 여기만 수정하면 됨
 */
export const HIGHLIGHT_ACCENT_MAP = {
  // table: "orange",
  // figure: "violet",
  // chart: "magenta",
  // heading1: "cyan",
  // header: "cyan",
  // caption: "yellow",
  // paragraph: "blue",
  // equation: "green",
  // footnote: "red",
  // footer: "red",
  // list: "gray",
  // index: "gray",
  // default: "gray",
  table: "blue",
  figure: "blue",
  chart: "blue",
  heading1: "blue",
  header: "blue",
  caption: "blue",
  paragraph: "blue",
  equation: "blue",
  footnote: "blue",
  footer: "blue",
  list: "blue",
  index: "blue",
  default: "blue",
} as const satisfies Record<string, AccentColor>;

// Preview/DP 전반에서 공통으로 사용할 하이라이트 키 타입
export type HighlightKey = keyof typeof HIGHLIGHT_ACCENT_MAP;

/**
 * 공통 템플릿: accent 색상만 바꿔서 일관된 하이라이트 스타일을 만들 수 있도록 헬퍼 함수로 분리
 */
const createHighlightStyle = (accent: AccentColor): HighlightStyle => ({
  containerHover: [
    `bg-accent-${accent}-background/50`,
    "outline",
    `outline-accent-${accent}/50`,
    "outline-1",
    `hover:bg-accent-${accent}-background`,
    "hover:outline",
    `hover:outline-accent-${accent}`,
    "hover:outline-1",
  ].join(" "),
  containerSelected: [
    `bg-accent-${accent}-background`,
    "outline",
    `outline-accent-${accent}`,
    "outline-1",
  ].join(" "),
  labelHover: [
    `text-accent-${accent}`,
    "bg-background",
    `border-accent-${accent}`,
  ].join(" "),
  labelSelected: [`bg-accent-${accent}`, "text-background"].join(" "),
});

export const HIGHLIGHT_STYLES: Record<string, HighlightStyle> =
  Object.fromEntries(
    Object.entries(HIGHLIGHT_ACCENT_MAP).map(([highlightKey, accent]) => [
      highlightKey,
      createHighlightStyle(accent as AccentColor),
    ]),
  ) as Record<string, HighlightStyle>;

/**
 * 하이라이트 키(예: category 이름)로 스타일 조회 (없으면 default 사용)
 */
export const getHighlightStyle = (highlightKey: string): HighlightStyle =>
  HIGHLIGHT_STYLES[highlightKey] ?? HIGHLIGHT_STYLES.default;

/**
 * 하이라이트 키(예: category 이름)로 Tailwind accent 토큰을 생성
 * - 예: "table" → "accent-orange"
 */
export const getHighlightAccentToken = (
  highlightKey: string,
): string | undefined => {
  const accent = HIGHLIGHT_ACCENT_MAP[highlightKey as HighlightKey];
  return accent ? `accent-${accent}` : undefined;
};
